import { useCallback, useState } from "react";
import styled from "@emotion/styled";
import { theme } from "../lib/theme";

/**
 * Copy a whole column of addresses in one go.
 *
 * Per-row copy (see `HexDisplay`) answers "give me this validator"; this answers "give me the
 * list", which is the shape the addresses are actually wanted in — pasted into a validator
 * dashboard, a `grep -f` over sequencer logs, or a spreadsheet next to last week's roster.
 * Twenty rows copied one at a time is not the same feature.
 *
 * Deliberately the full result set, not the page on screen. The tables here page at 25 purely to
 * keep the DOM small, so copying only the visible slice would silently drop the rest of a list the
 * reader can see the size of — the count on the button is the promise it keeps.
 */
const Btn = styled.button<{ done: boolean }>`
  background: none;
  border: 1px solid ${(p) => (p.done ? theme.colors.success : theme.colors.border)};
  border-radius: ${theme.radius.sm};
  color: ${(p) => (p.done ? theme.colors.success : theme.colors.textMuted)};
  cursor: pointer;
  padding: 2px 8px;
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  transition:
    color 0.15s,
    border-color 0.15s;

  &:hover {
    color: ${(p) => (p.done ? theme.colors.success : theme.colors.text)};
    border-color: ${(p) => (p.done ? theme.colors.success : theme.colors.primary)};
  }
`;

/** One address per line: the form every tool that takes a list of addresses expects. */
function addressListText(addresses: string[]): string {
  return addresses.join("\n");
}

export interface CopyAddressListProps {
  addresses: string[];
  /**
   * Plural noun for what is being copied — "proposers", "validators". Named on the button rather
   * than left to context: a page can carry more than one of these, and two buttons both reading
   * "copy 42 addresses" say nothing about which list each one takes.
   */
  noun: string;
  className?: string;
}

export function CopyAddressList({ addresses, noun, className }: CopyAddressListProps) {
  // Three states rather than two: a clipboard write can be refused outright (an insecure origin
  // has no `navigator.clipboard` at all), and a button that looks like it worked is worse than one
  // that admits it didn't — the reader would paste stale content and never know.
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(addressListText(addresses));
      setState("copied");
    } catch {
      setState("failed");
    }
    setTimeout(() => setState("idle"), 1500);
  }, [addresses]);

  if (addresses.length === 0) return null;

  return (
    <Btn
      className={className}
      done={state === "copied"}
      onClick={copy}
      title={`Copy all ${addresses.length} ${noun} addresses, one per line`}
    >
      {state === "copied" && `✓ copied ${addresses.length}`}
      {state === "failed" && "✗ copy failed"}
      {state === "idle" && `⧉ copy ${addresses.length} ${noun}`}
    </Btn>
  );
}
